import React from 'react';
import { useTranslation } from 'react-i18next';
import U2000BasicDialog from '../../../../u2000Core/components/dialog/U2000BasicDialog';
import { NS } from '../../../U3141';
import type { U3141ChainPdfAlerte } from '../../../U3141Service';
import { u3141FormatChainPdfAlerteLabel } from '../../../tools/u3141ChainPdfAlerteDisplay';

export interface U3141ChainPdfAlertesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  alertes: U3141ChainPdfAlerte[];
  ficheCount?: number;
}

export const U3141ChainPdfAlertesDialog: React.FC<U3141ChainPdfAlertesDialogProps> = ({
  isOpen,
  onClose,
  alertes,
  ficheCount,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      title={t('dlgChainPdfAlertesTitle', { ns: NS })}
      width="large"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full">
        <section className="flex-1 px-6 py-4 overflow-y-auto min-h-0">
          {typeof ficheCount === 'number' ? (
            <p className="mb-3 text-sm text-gray-600">
              {t('dlgChainPdfAlertesIntro', { ns: NS, ficheCount, alerteCount: alertes.length })}
            </p>
          ) : (
            <p className="mb-3 text-sm text-gray-600">
              {t('dlgChainPdfAlertesIntroNoFiche', { ns: NS, alerteCount: alertes.length })}
            </p>
          )}
          {alertes.length === 0 ? (
            <p className="text-sm text-gray-500">{t('dlgChainPdfAlertesEmpty', { ns: NS })}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      {t('lblChainPdfAlerteType', { ns: NS })}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      {t('lblChainPdfAlerteLot', { ns: NS })}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      {t('lblChainPdfAlerteExplication', { ns: NS })}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {alertes.map((alerte, idx) => {
                    const lotLabel =
                      alerte.lot ??
                      (Array.isArray(alerte.lots) && alerte.lots.length > 0 ? alerte.lots.join(', ') : '—');
                    return (
                      <tr key={`${alerte.type}-${alerte.lot ?? ''}-${idx}`}>
                        <td className="px-3 py-2 align-top text-gray-800 whitespace-nowrap">
                          {u3141FormatChainPdfAlerteLabel(String(alerte.type), t, NS)}
                        </td>
                        <td className="px-3 py-2 align-top text-gray-800 font-mono text-xs">{lotLabel}</td>
                        <td className="px-3 py-2 align-top text-gray-700">{alerte.explication}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50"
            onClick={() => onClose()}
          >
            {t('btnChainPdfAlertesClose', { ns: NS })}
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
